import { COLORS } from '@monthio/react-components'
import styled, { css, keyframes } from 'styled-components'
import React, { useEffect, useRef } from 'react'

const SliderArea = styled.div`
	display: grid;
	grid-template-areas: 'stack';
	overflow: hidden;
	width: 100%;
	height: 100%;
`

const slideout = (direction: 'forward' | 'backward') => keyframes`
	0% {
		translate: 0;
	}
	100% {
		translate: ${direction === 'forward' ? -120 : 120}%;
	}
`

const slidein = (direction: 'forward' | 'backward') => keyframes`
	0% {
		translate: ${direction === 'forward' ? 120 : -120}%;
	}
	100% {
		translate: 0%;
	}
`

const SlidingElementContainer = styled.div<{
	$isCurrent: boolean
	$durationMs: number
	$direction: 'forward' | 'backward'
	$canAnimate: boolean
	$isFirstFrame: boolean
}>`
	grid-area: stack;
	flex-direction: column;
	background: ${COLORS.BACKGROUND};
	overflow: hidden;

	${(p) =>
		p.$canAnimate &&
		!p.$isFirstFrame &&
		css`
			animation: ${p.$isCurrent
					? slidein(p.$direction)
					: slideout(p.$direction)}
				${p.$durationMs}ms forwards;
		`}

	@media (prefers-reduced-motion) {
		animation: none;
	}
`

const SlidingElement: React.FC<{
	isCurrent: boolean
	durationMs: number
	direction: 'forward' | 'backward'
	canAnimate: boolean
	isFirstFrame: boolean
}> = ({
	isCurrent,
	direction,
	canAnimate,
	isFirstFrame,
	durationMs,
	...props
}) => {
	/**
	 * Discrete animation of display doesn't work in firefox, so
	 * we set it up with a ref and animation start/end,
	 * unless the user prefers reduced motion, in case
	 * we display the individual pages.
	 */
	const ref = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (ref.current) {
			if (isCurrent) {
				ref.current.style.display = 'block'
			} else if (
				!canAnimate ||
				(isFirstFrame && !isCurrent) ||
				window.matchMedia('(prefers-reduced-motion)')?.matches
			) {
				ref.current.style.display = 'none'
			}
		}
	}, [isCurrent, canAnimate, isFirstFrame])

	return (
		<SlidingElementContainer
			{...props}
			ref={ref}
			aria-hidden={!isCurrent}
			$isCurrent={isCurrent}
			$direction={direction}
			$canAnimate={canAnimate}
			$isFirstFrame={isFirstFrame}
			$durationMs={durationMs}
			hidden={!canAnimate}
			onAnimationStart={() => {
				if (isCurrent && ref.current) {
					ref.current.style.display = 'block'
				}
			}}
			onAnimationEnd={() => {
				if (!isCurrent && ref.current) {
					ref.current.style.display = 'none'
				}
			}}
		/>
	)
}

export function Slider<TKey extends string | number>({
	items,
	durationMs,
	currentKey,
}: {
	items: { key: TKey; element: React.ReactNode }[]
	durationMs: number
	currentKey: TKey
}): JSX.Element {
	const currentIndex = items.findIndex(({ key }) => key === currentKey)

	const ref = useRef<number>()
	useEffect(() => {
		ref.current = currentIndex
	}, [currentIndex])

	const lastIndex = ref.current || 0
	const direction = lastIndex <= currentIndex ? 'forward' : 'backward'

	const isFirstFrame = ref.current === undefined || lastIndex === currentIndex

	return (
		<SliderArea>
			{items.map(({ key, element }, index) => (
				<SlidingElement
					key={key}
					isCurrent={key === currentKey}
					durationMs={durationMs}
					direction={direction}
					canAnimate={index === lastIndex || index === currentIndex}
					isFirstFrame={isFirstFrame}
				>
					{element}
				</SlidingElement>
			))}
		</SliderArea>
	)
}

export const flowContainerWithFixedButtonsDirectParentCssHack = css`
	> div {
		padding: 20px 12px;
	}

	@media (min-width: 600px) {
		> div {
			padding: 44px 28px 20px;
		}
	}
`
